/**
 * FirstRunBackdrop — the wash behind every first-run screen.
 *
 * Four blurred colour pools drifting on the step's palette, a grain
 * overlay, a vignette, two twinkling star layers and a fade to ink at
 * the bottom where the text and button sit. The palette transitions
 * (1.2s) as the step changes, so moving through setup reads as one
 * room changing light rather than a sequence of pages.
 */
import { FIRSTRUN_KEYFRAMES, PAL } from './tokens';

const NOISE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")";

const STARS_A =
  'radial-gradient(1.2px 1.2px at 12% 9%,rgba(255,255,255,.8),transparent 60%),radial-gradient(1px 1px at 37% 15%,rgba(255,255,255,.6),transparent 60%),radial-gradient(1.3px 1.3px at 72% 7%,rgba(255,255,255,.8),transparent 60%),radial-gradient(1px 1px at 89% 21%,rgba(255,255,255,.6),transparent 60%),radial-gradient(1px 1px at 6% 33%,rgba(255,255,255,.5),transparent 60%),radial-gradient(1.2px 1.2px at 58% 27%,rgba(255,255,255,.5),transparent 60%)';
const STARS_B =
  'radial-gradient(1px 1px at 24% 22%,rgba(255,255,255,.7),transparent 60%),radial-gradient(1.2px 1.2px at 46% 5%,rgba(255,255,255,.7),transparent 60%),radial-gradient(1px 1px at 81% 36%,rgba(255,255,255,.5),transparent 60%),radial-gradient(1px 1px at 95% 11%,rgba(255,255,255,.6),transparent 60%)';

export function paletteFor(key: string) {
  const pv = PAL[key] ?? PAL.s1;
  return { base: pv[0], a: pv[1], b: pv[2], c: pv[3] };
}

export function FirstRunBackdrop({ palette }: { palette: string }) {
  const pal = paletteFor(palette);
  const t = 'background-color 1.2s ease';
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <style>{FIRSTRUN_KEYFRAMES}</style>
      <div
        className="absolute rounded-full mix-blend-screen"
        style={{ left: '-18%', top: '-16%', width: 'max(78%,460px)', aspectRatio: '1', backgroundColor: pal.a, filter: 'blur(90px)', transition: t, animation: 'frDrift1 22s ease-in-out infinite' }}
      />
      <div
        className="absolute rounded-full mix-blend-screen"
        style={{ right: '-22%', top: '4%', width: 'max(70%,420px)', aspectRatio: '1', backgroundColor: pal.b, filter: 'blur(100px)', opacity: 0.95, transition: t, animation: 'frDrift2 27s ease-in-out infinite' }}
      />
      <div
        className="absolute rounded-full"
        style={{ left: '8%', top: '30%', width: 'max(84%,480px)', aspectRatio: '1', backgroundColor: pal.c, filter: 'blur(120px)', opacity: 0.82, transition: t, animation: 'frDrift3 31s ease-in-out infinite' }}
      />
      <div
        className="absolute rounded-full"
        style={{ left: '30%', bottom: '-30%', width: 'max(60%,380px)', aspectRatio: '1', backgroundColor: pal.c, filter: 'blur(110px)', opacity: 0.55, transition: t, animation: 'frDrift1 19s ease-in-out -7s infinite' }}
      />
      <div className="absolute inset-0 mix-blend-overlay opacity-50" style={{ backgroundImage: NOISE }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 40%,transparent 50%,rgba(0,0,0,.45) 100%)' }} />
      <div className="absolute inset-0" style={{ backgroundImage: STARS_A, animation: 'frTwinkle 6s ease-in-out infinite' }} />
      <div className="absolute inset-0" style={{ backgroundImage: STARS_B, animation: 'frTwinkle 8s ease-in-out -3s infinite' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg,rgba(11,7,9,0) 0%,rgba(11,7,9,.05) 44%,rgba(11,7,9,.72) 72%,#0B0709 90%)' }} />
    </div>
  );
}

/**
 * The big soft orb behind the stage. `data-a="orb"` so the motion hook
 * breathes it in on enter and lets it swell on swap; `data-ring` /
 * `data-wash` are the one-shot pulses that fire on every step change.
 */
export function StageOrb({ palette }: { palette: string }) {
  const pal = paletteFor(palette);
  const size = 'clamp(520px,60vw,900px)';
  return (
    <div data-a="orb" className="pointer-events-none absolute inset-0" aria-hidden>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: size, aspectRatio: '1' }}>
        <div className="relative h-full w-full" style={{ animation: 'frOrbPulse 4s ease-in-out infinite' }}>
          <div className="absolute rounded-full" style={{ inset: '16%', backgroundColor: pal.b, filter: 'blur(56px)', opacity: 0.8, transition: 'background-color 1.2s ease' }} />
          <div className="absolute rounded-full mix-blend-screen" style={{ inset: '24%', backgroundColor: pal.c, filter: 'blur(40px)', opacity: 0.55, transition: 'background-color 1.2s ease', animation: 'frOrbBreath 9s ease-in-out -3s infinite' }} />
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 50% 42%, rgba(250,245,255,.55) 0%, rgba(235,222,255,.18) 30%, transparent 62%)',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.1), inset 0 30px 80px rgba(255,255,255,.06), inset 0 -40px 90px rgba(0,0,0,.25)',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              inset: '6%',
              background: 'conic-gradient(from 0deg, transparent 0 60%, rgba(255,255,255,.22) 72%, transparent 84%)',
              animation: 'frRingSpin 26s linear infinite',
              maskImage: 'radial-gradient(circle,transparent 62%,#000 64%,#000 66%,transparent 68%)',
              WebkitMaskImage: 'radial-gradient(circle,transparent 62%,#000 64%,#000 66%,transparent 68%)',
            }}
          />
          <div className="absolute rounded-full" style={{ inset: '-4%', boxShadow: `inset 0 0 0 1px ${pal.b}55`, transition: 'box-shadow 1.2s' }} />
          <div className="absolute rounded-full opacity-0" style={{ inset: '20%', boxShadow: `0 0 0 2px ${pal.b}, 0 0 44px ${pal.b}`, animation: 'frRingOut 4s ease-out infinite' }} />
          <div data-ring="1" className="absolute rounded-full opacity-0" style={{ inset: '12%', boxShadow: `0 0 0 3px ${pal.b}, 0 0 90px ${pal.b}, inset 0 0 70px ${pal.b}` }} />
          <div data-wash="1" className="absolute rounded-full opacity-0 mix-blend-screen" style={{ inset: '-30%', background: `radial-gradient(circle, ${pal.c} 0%, ${pal.b}88 35%, transparent 70%)` }} />
          <div data-ring="2" className="absolute rounded-full opacity-0" style={{ inset: '12%', boxShadow: `0 0 0 2px ${pal.c}, 0 0 60px ${pal.c}` }} />
        </div>
      </div>
    </div>
  );
}

/** Six small sparkles drifting over the stage. */
export function StageSparks({ accent }: { accent: string }) {
  const sparks = [
    { x: '8%', y: '18%', s: '14px', d: '5s', dl: '0s' },
    { x: '86%', y: '12%', s: '10px', d: '6.5s', dl: '-2s' },
    { x: '92%', y: '58%', s: '16px', d: '7s', dl: '-1s' },
    { x: '14%', y: '70%', s: '11px', d: '5.5s', dl: '-3s' },
    { x: '48%', y: '6%', s: '9px', d: '6s', dl: '-4s' },
    { x: '70%', y: '82%', s: '13px', d: '7.5s', dl: '-2.5s' },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {sparks.map((sp, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          className="absolute"
          style={{ left: sp.x, top: sp.y, width: sp.s, height: sp.s, animation: `frSparkle ${sp.d} ease-in-out ${sp.dl} infinite` }}
          fill={i % 2 ? accent : '#F2EFEB'}
        >
          <path d="M12 0c.6 6.5 5.5 11.4 12 12-6.5.6-11.4 5.5-12 12-.6-6.5-5.5-11.4-12-12C6.5 11.4 11.4 6.5 12 0z" />
        </svg>
      ))}
    </div>
  );
}
